import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { ChevronDown, Check, Search } from "lucide-react";

export type SelectMenuOption = { value: string; label: string };

type SelectMenuSize = "sm" | "md";

type SelectMenuProps = {
  value: string;
  onChange: (value: string) => void;
  options: SelectMenuOption[];
  searchable?: boolean;
  searchThreshold?: number;
  placeholder?: string;
  className?: string;
  size?: SelectMenuSize;
  disabled?: boolean;
  "aria-label"?: string;
};

const TRIGGER_SIZES = {
  md: "px-3 py-2 text-sm",
  sm: "px-2 py-1 text-xs",
} as const;

export default function SelectMenu({
  value,
  onChange,
  options,
  searchable,
  searchThreshold = 8,
  placeholder = "Select...",
  className = "",
  size = "md",
  disabled = false,
  "aria-label": ariaLabel,
}: SelectMenuProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [coords, setCoords] = useState<{
    top?: number;
    bottom?: number;
    left: number;
    width: number;
    maxHeight: number;
  } | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const isSearchable = searchable ?? options.length > searchThreshold;
  const selected = options.find((o) => o.value === value);

  const filtered = useMemo(() => {
    if (!isSearchable || !query.trim()) return options;
    const q = query.toLowerCase();
    return options.filter((o) => o.label.toLowerCase().includes(q));
  }, [options, query, isSearchable]);

  useLayoutEffect(() => {
    if (!open) return;
    function place() {
      const el = triggerRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      const margin = 8;
      const spaceBelow = window.innerHeight - r.bottom - margin;
      const spaceAbove = r.top - margin;
      const openUp = spaceBelow < 220 && spaceAbove > spaceBelow;
      const maxHeight = Math.min(320, Math.max(openUp ? spaceAbove : spaceBelow, 120));
      if (openUp) {
        setCoords({ bottom: window.innerHeight - r.top + 4, left: r.left, width: r.width, maxHeight });
      } else {
        setCoords({ top: r.bottom + 4, left: r.left, width: r.width, maxHeight });
      }
    }
    place();
    window.addEventListener("scroll", place, true);
    window.addEventListener("resize", place);
    return () => {
      window.removeEventListener("scroll", place, true);
      window.removeEventListener("resize", place);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    function onDown(e: MouseEvent) {
      const t = e.target as Node;
      if (triggerRef.current?.contains(t) || menuRef.current?.contains(t)) return;
      setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") setOpen(false);
    }
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  function toggle() {
    if (disabled) return;
    setQuery("");
    setOpen((o) => !o);
  }

  function select(v: string) {
    onChange(v);
    setOpen(false);
  }

  return (
    <div className={`relative ${className}`}>
      <button
        ref={triggerRef}
        type="button"
        onClick={toggle}
        disabled={disabled}
        aria-label={ariaLabel}
        className={`flex h-full w-full items-center justify-between gap-2 rounded-lg border border-gray-300 bg-white text-gray-900 hover:border-gray-400 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:cursor-not-allowed disabled:bg-gray-100 disabled:text-gray-500 ${TRIGGER_SIZES[size]}`}
      >
        <span className={`truncate ${selected ? "" : "text-gray-400"}`}>{selected?.label ?? placeholder}</span>
        <ChevronDown size={16} className={`shrink-0 text-gray-400 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && coords &&
        createPortal(
          <div
            ref={menuRef}
            style={{
              position: "fixed",
              left: coords.left,
              minWidth: Math.max(coords.width, 144),
              maxHeight: coords.maxHeight,
              ...(coords.top !== undefined ? { top: coords.top } : { bottom: coords.bottom }),
            }}
            className="z-50 flex flex-col rounded-lg border border-gray-200 bg-white py-1 shadow-lg"
          >
            {isSearchable && (
              <div className="px-2 pb-1">
                <div className="relative">
                  <Search size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input
                    autoFocus
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Escape") setOpen(false);
                      if (e.key === "Enter" && filtered.length > 0) select(filtered[0].value);
                    }}
                    placeholder="Search..."
                    className="w-full rounded-md border border-gray-200 bg-gray-50 py-1.5 pl-7 pr-2 text-sm text-gray-900 focus:border-blue-500 focus:bg-white focus:outline-none"
                  />
                </div>
              </div>
            )}
            <ul className="min-h-0 flex-1 overflow-y-auto">
              {filtered.map((o) => {
                const active = o.value === value;
                return (
                  <li key={o.value}>
                    <button
                      type="button"
                      onClick={() => select(o.value)}
                      className={`flex w-full items-center justify-between gap-2 px-3 py-1.5 text-left text-sm transition-colors ${
                        active ? "bg-blue-50 text-blue-700" : "text-gray-800 hover:bg-gray-50"
                      }`}
                    >
                      <span className="truncate">{o.label}</span>
                      {active && <Check size={14} className="shrink-0 text-blue-600" />}
                    </button>
                  </li>
                );
              })}
              {filtered.length === 0 && <li className="px-3 py-2 text-sm text-gray-400">No matches</li>}
            </ul>
          </div>,
          document.body,
        )}
    </div>
  );
}
